export interface AuthUser {
  id: string
  email: string
  emailVerified: boolean
  pendingEmail: string | null
  notificationEmail: string | null
  notificationEmailVerified: boolean
  displayName: string
  userIconUrl: string | null
  userCoverUrl: string | null
}

export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  email: string
  password: string
  displayName: string
}

export interface UpdateProfileRequest {
  displayName: string
  bio?: string | null
  twitterUrl?: string | null
  githubUrl?: string | null
  websiteUrl?: string | null
  youtubeUrl?: string | null
  discordUsername?: string | null
}

export interface AuthResponse {
  user: AuthUser
  message?: string
  emailVerificationRequired?: boolean
}

/**
 * トークンリフレッシュの結果
 * - refreshed: 新しいトークンの取得に成功
 * - unauthorized: リフレッシュトークンが欠落/無効/期限切れ（429 を除く 4xx）。認証状態をクリアすべき
 * - banned: アカウント停止（403 ACCOUNT_BANNED）。認証状態をクリアし BAN 表示にする
 * - transient-error: 一過性の失敗（5xx・429・ネットワークエラー等）。認証状態は保持し後で再試行する
 * - no-session: そもそもログイン中でない（キャッシュユーザー無し）。サーバーには問い合わせていない
 */
export type RefreshResult =
  | 'refreshed'
  | 'unauthorized'
  | 'banned'
  | 'transient-error'
  | 'no-session'

/** backend がアカウント停止を示す code（HTTP 403 と組） */
export const ACCOUNT_BANNED_CODE = 'ACCOUNT_BANNED'
/** リフレッシュの一過性失敗を示す AuthError の code */
export const REFRESH_TRANSIENT_ERROR_CODE = 'REFRESH_TRANSIENT_ERROR'
/** BAN 検出を React 側（AuthContext）へ伝える window イベント名 */
export const AUTH_BANNED_EVENT = 'auth:banned'

export const ACCOUNT_BANNED_MESSAGE = 'アカウントが停止されています。'
export const LOGIN_REQUIRED_MESSAGE = 'ログインが必要です。'
export const SESSION_EXPIRED_MESSAGE =
  'セッションの有効期限が切れました。再度ログインしてください。'

export interface VRoidAuthUrlResponse {
  authUrl: string
  state: string
}

export class AuthError extends Error {
  code?: string

  constructor({ message, code }: { message: string; code?: string }) {
    super(message)
    this.name = 'AuthError'
    this.code = code
  }
}

export function createAccountBannedError(): AuthError {
  return new AuthError({ message: ACCOUNT_BANNED_MESSAGE, code: ACCOUNT_BANNED_CODE })
}
